import { type ReactElement, type ReactNode, useRef, useState } from "react";

import { alpha } from "@mui/material";
import Menu from "@mui/material/Menu";
import { type PopoverActions } from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";

type ToolsMenuApi = {
  onMenuItemClick: () => void;
  updatePosition: () => void;
};

type Props = {
  buttonId?: string;
  children: (api: ToolsMenuApi) => ReactNode;
  icon: ReactElement;
  onClick?: () => void;
  tooltipMessage?: ReactNode;
};

const ToolsMenuControl = ({
  buttonId = "",
  children,
  icon,
  onClick,
  tooltipMessage = "",
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const actionsRef = useRef<PopoverActions | null>(null);
  const classes = useStyles();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const updatePosition = () => {
    actionsRef.current?.updatePosition();
  };

  return (
    <div>
      <Tooltip
        classes={{ tooltip: classes.tooltip }}
        placement="bottom"
        title={tooltipMessage}
      >
        <div
          {...(buttonId ? { id: buttonId } : {})}
          className={classes.toolbarMenuItem}
          onClick={handleClick}
          role="button"
          style={{ color: open ? "#FFFFFF" : undefined }}
          tabIndex={0}
        >
          {icon}
        </div>
      </Tooltip>
      <Menu
        action={actionsRef}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        disableScrollLock={true}
        onClose={handleClose}
        open={open}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {children({ onMenuItemClick: handleClose, updatePosition })}
      </Menu>
    </div>
  );
};

const useStyles = makeStyles({
  toolbarMenuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: alpha("#FFFFFF", 0.7),
    width: 40,
    "&:hover": {
      color: "#FFFFFF",
    },
  },
  tooltip: {
    margin: 4,
  },
});

export { ToolsMenuControl, type ToolsMenuApi };
